import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Zap } from "lucide-react";
import { onPaywallOpen, closePaywall } from "../lib/paywall";
import { useFeatureFlags } from "../lib/feature_flags";

/**
 * Global hard-paywall modal.
 *
 * Listens for `paywall:open` events from the axios 402 interceptor and
 * renders a full-screen blocker that redirects the user to /subscriptions.
 * Two states: "no_subscription" (first run) and "out_of_credits"
 * (period balance drained). Both close with a Subscribe / Upgrade CTA;
 * dismissing without subscribing simply hides the modal so users can
 * navigate around the app (read-only).
 */
export default function PaywallModal() {
    const navigate = useNavigate();
    const [state, setState] = useState(null); // {code, message, balance, needed_credits}
    const flags = useFeatureFlags();
    const showSubscriptions = flags.show_subscriptions !== false;

    useEffect(() => {
        const off = onPaywallOpen((detail) => setState(detail || {}));
        return off;
    }, []);

    // iter-108: when the subscriptions feature is disabled the paywall
    // should never appear — backend won't 402 admin/superadmin anyway,
    // but flipping the flag mid-session covers regular users too.
    if (!showSubscriptions) return null;
    if (!state) return null;

    const code = state.code || "out_of_credits";
    const isNoSub = code === "no_subscription";
    const balance = state.balance || {};
    const needed = Number(state.needed_credits || 0);

    const handleClose = () => {
        setState(null);
        closePaywall();
    };
    const handleSubscribe = () => {
        handleClose();
        navigate("/subscriptions");
    };

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            data-testid="paywall-modal"
            onClick={handleClose}
        >
            <div
                className="w-full max-w-md bg-[#0F1B3A] border border-[#2A3655] rounded-md shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative px-5 py-4 border-b border-[#2A3655] bg-gradient-to-r from-[#0A1128] via-[#1C253C] to-[#0A1128]">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#00A3FF]" />
                        <h3 className="text-base font-bold uppercase tracking-wider text-white">
                            {isNoSub ? "Subscribe to use AI" : "Out of credits"}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        data-testid="paywall-close"
                        className="absolute top-3 right-3 text-slate-400 hover:text-white"
                        aria-label="Close paywall"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-sm text-slate-300">
                        {state.message ||
                            (isNoSub
                                ? "AI features require an active subscription. Pick a plan to get started."
                                : "You've used all of this period's AI credits. Upgrade your plan or wait for the next renewal.")}
                    </p>
                    {!isNoSub && balance.credit_amount > 0 && (
                        <div className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                                Your balance
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-300">
                                    {balance.tier_title || "—"}
                                </span>
                                <span className="tabular-nums text-amber-400 font-bold">
                                    {Number(balance.credits_remaining || 0).toLocaleString()}
                                    <span className="text-slate-500 font-normal">
                                        {" "}
                                        / {Number(balance.credit_amount).toLocaleString()}
                                    </span>
                                </span>
                            </div>
                            {needed > 0 && (
                                <div className="text-[11px] text-slate-500 mt-1">
                                    This call needs{" "}
                                    <span className="text-white font-semibold">
                                        {needed} credits
                                    </span>
                                    .
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#2A3655] bg-[#0A1128]">
                    <button
                        type="button"
                        onClick={handleClose}
                        data-testid="paywall-dismiss"
                        className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white"
                    >
                        Not now
                    </button>
                    <button
                        type="button"
                        onClick={handleSubscribe}
                        data-testid="paywall-subscribe"
                        className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white inline-flex items-center gap-1.5"
                    >
                        <Zap className="w-3 h-3" />
                        {isNoSub ? "View plans" : "Upgrade"}
                    </button>
                </div>
            </div>
        </div>
    );
}
